import type { Utilisateur } from './api/types';

// Session locale : le jeton rendu par l'API et l'utilisateur connecte.
//
// Choix du stockage : localStorage. sessionStorage serait perdu a chaque fermeture, ce
// qui obligerait a se reconnecter en permanence — inacceptable pour une application
// posee sur l'ecran d'accueil. Un cookie httpOnly serait plus sur contre le vol par
// script, mais l'API rend son jeton dans le corps de la reponse, pas en cookie ; le
// changer touche au serveur et depasse ce lot. La page ne charge aucun script tiers,
// ce qui reduit d'autant la surface.

const CLE_JETON = 'colette.jeton';
const CLE_UTILISATEUR = 'colette.utilisateur';
const CLE_ACCUEIL_VU = 'colette.accueil-vu';

// En navigation privee, ou si l'utilisateur a bloque le stockage, chaque acces peut
// lever. Colette doit alors continuer de fonctionner le temps de la session en cours
// plutot que de refuser de demarrer.
function lire(cle: string): string | null {
  try {
    return window.localStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string): void {
  try {
    window.localStorage.setItem(cle, valeur);
  } catch {
    /* stockage indisponible : on continue sans persistance */
  }
}

function retirer(cle: string): void {
  try {
    window.localStorage.removeItem(cle);
  } catch {
    /* idem */
  }
}

export function lireJeton(): string | null {
  return lire(CLE_JETON);
}

export function lireUtilisateur(): Utilisateur | null {
  const brut = lire(CLE_UTILISATEUR);
  if (brut === null) return null;
  try {
    const valeur = JSON.parse(brut) as unknown;
    // Un contenu corrompu (ecriture partielle, ancienne version) ne doit pas faire
    // planter le demarrage : on le traite comme une absence de session.
    if (
      typeof valeur === 'object' &&
      valeur !== null &&
      typeof (valeur as Utilisateur).id === 'string' &&
      typeof (valeur as Utilisateur).displayName === 'string'
    ) {
      return valeur as Utilisateur;
    }
    return null;
  } catch {
    return null;
  }
}

export function enregistrerSession(jeton: string, utilisateur: Utilisateur): void {
  ecrire(CLE_JETON, jeton);
  ecrire(CLE_UTILISATEUR, JSON.stringify(utilisateur));
}

export function effacerSession(): void {
  retirer(CLE_JETON);
  retirer(CLE_UTILISATEUR);
}

/**
 * Le serveur vient de refuser le jeton EN COURS D'USAGE (audit du 2026-09-27).
 *
 * Effacer la session ne suffit pas : l'application restait sur ses ecrans, devant un
 * « Réessayer » qui ne pouvait plus reussir — et une application posee sur l'ecran
 * d'accueil n'a pas de bouton pour recharger. Cet evenement previent `App`, qui ramene
 * a la connexion en disant pourquoi.
 *
 * Un evenement plutot qu'un appel direct : `api/client.ts` ne connait pas l'interface,
 * et ne doit pas la connaitre.
 */
export const SESSION_PERDUE = 'colette:session-perdue';

export function signalerSessionPerdue(): void {
  try {
    window.dispatchEvent(new Event(SESSION_PERDUE));
  } catch {
    /* prevenir ne doit jamais faire echouer l'appel qui a revele la perte */
  }
}

export function accueilDejaVu(): boolean {
  return lire(CLE_ACCUEIL_VU) === 'oui';
}

export function marquerAccueilVu(): void {
  ecrire(CLE_ACCUEIL_VU, 'oui');
}

/** Prenom d'affichage : premier mot du nom du compte. */
export function prenomDe(utilisateur: Utilisateur | null): string {
  if (utilisateur === null) return '';
  return utilisateur.displayName.trim().split(/\s+/)[0] ?? utilisateur.displayName;
}
